// React imports
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

// External library imports
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import axios from "axios";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { viewActiveUsers, assignKRAUrl, managerAssignmentsUrl } from "../../../Service/Apis";
import { Employee } from "./Types/AssignKRATypes";

const AssignKRAForm: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  // 1. useState declarations
  const [assignmentType, setAssignmentType] = useState<"all" | "specific" | "other">("all");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [otherEmployees, setOtherEmployees] = useState<Employee[]>([]);
  const [filteredOtherEmployees, setFilteredOtherEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [managerSelections, setManagerSelections] = useState<Record<string, { m1: boolean; m2: boolean }>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

// 2. const variables (hooks, Redux, computed constants)
  const [reviewPeriod, setReviewPeriod] = useState<string>(
    location.state?.reviewPeriod || "quarterly"
  );
  const [customFromDate, setCustomFromDate] = useState<string>(
    location.state?.customFromDate || ""
  );
  const [customToDate, setCustomToDate] = useState<string>(
    location.state?.customToDate || ""
  );
  const designationName = location.state?.designationName || "Unknown";
  const formData = location.state?.formData;

  // 3. Function declarations
  const fetchEmployees = async (): Promise<void> => {
    setLoading(true);
    try {
      const [response, assignments] = await Promise.all([
        dispatch(fetchData(viewActiveUsers)),
        dispatch(fetchData(managerAssignmentsUrl)).catch(() => []),
      ]);
        if (response && Array.isArray(response)) {
        const assignArray: any[] = Array.isArray(assignments) ? assignments : [];

        const withManagers = (emp: Employee) => {
          const m1 = assignArray.find(
            (a: any) => a.employee === emp.id && a.manager_level === "M1" && a.is_active
          );
          const m2 = assignArray.find(
            (a: any) => a.employee === emp.id && a.manager_level === "M2" && a.is_active
          );
          return {
            ...emp,
            m1_manager_name: m1?.manager_name || null,
            m2_manager_name: m2?.manager_name || null,
          };
        };

        const filtered = response
          .filter((emp: Employee) => emp.designation === designationName)
          .map(withManagers);

        const others = response
          .filter((emp: Employee) => emp.designation !== designationName)
          .map(withManagers)
          .sort((a: Employee, b: Employee) =>
            `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
          );

        // Default: both managers checked if they exist
        const initialSelections: Record<string, { m1: boolean; m2: boolean }> = {};
        [...filtered, ...others].forEach((emp) => {
          initialSelections[emp.id] = {
            m1: !!emp.m1_manager_name,
            m2: !!emp.m2_manager_name,
          };
        });
        setManagerSelections(initialSelections);
        setEmployees(filtered);
        setOtherEmployees(others);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load employees";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (): void => {
    const applySearch = (list: Employee[]) => {
      if (!searchQuery.trim()) return list;
      const query = searchQuery.toLowerCase();
      return list.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(query) ||
          emp.last_name?.toLowerCase().includes(query) ||
          emp.employee_code?.toLowerCase().includes(query) ||
          emp.email?.toLowerCase().includes(query) ||
          emp.designation?.toLowerCase().includes(query) ||
          emp.m1_manager_name?.toLowerCase().includes(query) ||
          emp.m2_manager_name?.toLowerCase().includes(query)
      );
    };

    setFilteredEmployees(applySearch([...employees]));
    setFilteredOtherEmployees(applySearch([...otherEmployees]));
  };

  const activeFilteredList = assignmentType === "other" ? filteredOtherEmployees : filteredEmployees;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const allIds = new Set(activeFilteredList.map((emp) => emp.id));
      setSelectedEmployees(allIds);
    } else {
      setSelectedEmployees(new Set());
    }
  };

  const handleSelectEmployee = (employeeId: string): void => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
  };

  const handleToggleManager = (
    employeeId: string,
    level: "m1" | "m2",
    checked: boolean
  ): void => {
    setManagerSelections((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [level]: checked,
      },
    }));
  };

  const handleAssign = async (): Promise<void> => {
    let employeeIds: string[] = [];

    if (assignmentType === "all") {
      employeeIds = employees.map((emp) => emp.id);
    } else {
      employeeIds = Array.from(selectedEmployees);
    }

    if (employeeIds.length === 0) {
      showToast("Please select at least one employee", "error", true);
      return;
    }

    // Validate: every selected employee must have at least one manager ticked
    if (assignmentType === "specific" || assignmentType === "other") {
      const allEmpPool = [...employees, ...otherEmployees];
      const employeesWithNoManager = employeeIds.filter((empId) => {
        const emp = allEmpPool.find((e) => e.id === empId);
        const hasBothManagers = emp?.m1_manager_name && emp?.m2_manager_name;
        const hasM1Only = emp?.m1_manager_name && !emp?.m2_manager_name;
        const sel = managerSelections[empId];
        if (hasBothManagers) return !sel?.m1 && !sel?.m2;
        if (hasM1Only) return !sel?.m1;
        return false; // employee has no managers assigned at all — allow through
      });

      if (employeesWithNoManager.length > 0) {
        const names = employeesWithNoManager.map((empId) => {
          const emp = allEmpPool.find((e) => e.id === empId);
          return emp ? `${emp.first_name} ${emp.last_name}` : empId;
        });
        showToast(
          `Please select at least one manager for: ${names.join(", ")}`,
          "error",
          true
        );
        return;
      }
    }
    if (reviewPeriod === "custom") {
      if (!customFromDate || !customToDate) {
        showToast("Please select From Date and To Date for custom review period", "error", true);
        return;
      }
      if (new Date(customFromDate) > new Date(customToDate)) {
        showToast("From Date must be before To Date", "error", true);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: any = {
        template_id: templateId,
        employee_ids: employeeIds,
        review_period: reviewPeriod,
      };

      if (reviewPeriod === "custom" && customFromDate && customToDate) {
        payload.custom_from_date = customFromDate;
        payload.custom_to_date = customToDate;
      }

      // For "specific"/"other" mode, include per-employee manager selections
      if (assignmentType === "specific" || assignmentType === "other") {
        payload.employee_manager_selections = employeeIds.map((empId) => ({
          employee_id: empId,
          include_m1: managerSelections[empId]?.m1 ?? true,
          include_m2: managerSelections[empId]?.m2 ?? true,
        }));
      }

      
      const token = localStorage.getItem("token");
      const response = await axios.post(assignKRAUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.success) {
        const { created_count, skipped_count, total_requested } = response.data;
        
        let message = `KRA assigned successfully!\n`;
        message += `✓ Assigned: ${created_count} employee(s)\n`;
        if (skipped_count > 0) {
          message += `⚠ Skipped: ${skipped_count} (already assigned)\n`;
        }
        
        showToast(message, "success", true);
        
        // Navigate back to roles after short delay
        setTimeout(() => {
          navigate("/pms/designations");
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Failed to assign KRA");
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to assign KRA. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmitting(false);
    }
  };

  // 4. useEffect hooks (just above return)
  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [employees, otherEmployees, searchQuery]);

  // 5. Return JSX
  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/pms/designations")}
          sx={{ mb: 2 }}
        >
          Back to Roles
        </Button>
        <Box sx={{ mb: 3 }}>
          <PageTitle>Assign KRA</PageTitle>
        </Box>
        <Typography variant="body1" color="black">
          Template: <strong>{formData?.form_name || "KRA Template"}</strong>
        </Typography>
        <Typography variant="body2" color="black">
          Designation: <strong>{designationName}</strong>
        </Typography>
      </Box>
      {/* Review Period */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          📅 Review Period
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Review Period</InputLabel>
          <Select
            value={reviewPeriod}
            label="Review Period"
            onChange={(e) => setReviewPeriod(e.target.value)}
          >
            <MenuItem value="monthly">Monthly (1 Month)</MenuItem>
            <MenuItem value="quarterly">Quarterly (3 Months)</MenuItem>
            <MenuItem value="half_yearly">Half-Yearly (6 Months)</MenuItem>
            <MenuItem value="yearly">Yearly (12 Months)</MenuItem>
            <MenuItem value="custom">Custom Date</MenuItem>
          </Select>
          <FormHelperText>Choose timeline for this assignment (can differ from template default)</FormHelperText>
        </FormControl>
        {reviewPeriod === "custom" && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="From Date"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={customFromDate}
              onChange={(e) => setCustomFromDate(e.target.value)}
              helperText="Start date of the review period"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="To Date"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={customToDate}
              onChange={(e) => setCustomToDate(e.target.value)}
              helperText="End date of the review period"
              inputProps={{ min: customFromDate || undefined }}
              sx={{ minWidth: 180 }}
            />
          </Box>
        )}
      </Paper>

      {/* Assignment Options */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          👥 Select Employees
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={assignmentType}
            onChange={(e) => {
              setAssignmentType(e.target.value as "all" | "specific" | "other");
              setSelectedEmployees(new Set());
              setSearchQuery("");
            }}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">
                    Assign to All {designationName}s
                  </Typography>
                  <Typography variant="caption" color="black">
                    {employees.length} employee(s) will receive this KRA
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Assign to Specific {designationName}s</Typography>
                  <Typography variant="caption" color="black">
                    Select individual employees from the list below
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="other"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Assign to Other Employees</Typography>
                  <Typography variant="caption" color="black">
                    Select employees from other designations
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {(assignmentType === "specific" || assignmentType === "other") && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={
                  assignmentType === "other"
                    ? "Search by name, code, designation, or manager name..."
                    : "Search by name, code, email, or manager name..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            {/* Employee Table */}
            {activeFilteredList.length === 0 ? (
              <Alert severity="info">
                No employees found matching your filters.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          activeFilteredList.length > 0 &&
                          selectedEmployees.size === activeFilteredList.length
                        }
                        indeterminate={
                          selectedEmployees.size > 0 &&
                          selectedEmployees.size < activeFilteredList.length
                        }
                        onChange={handleSelectAll}
                      />
                    }
                    label={`Select All (${activeFilteredList.length})`}
                  />
                  <Typography variant="body2" color="black">
                    {selectedEmployees.size} selected
                  </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Employee Code</TableCell>
                        <TableCell>Name</TableCell>
                        {assignmentType === "other" ? (
                          <TableCell>Designation</TableCell>
                        ) : (
                          <TableCell>Email</TableCell>
                        )}
                        <TableCell>Manager 1</TableCell>
                        <TableCell>Manager 2</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeFilteredList.map((employee) => {
                        const isSelected = selectedEmployees.has(employee.id);
                        const sel = managerSelections[employee.id] ?? { m1: true, m2: true };
                        return (
                        <TableRow
                          key={employee.id}
                          hover
                          onClick={() => handleSelectEmployee(employee.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectEmployee(employee.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>{employee.employee_code}</TableCell>
                          <TableCell>
                            {employee.first_name} {employee.last_name}
                          </TableCell>
                          {assignmentType === "other" ? (
                            <TableCell>{employee.designation || "—"}</TableCell>
                          ) : (
                            <TableCell>{employee.email}</TableCell>
                          )}
                          <TableCell>
                            {employee.m1_manager_name ? (
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  size="small"
                                  checked={sel.m1}
                                  disabled={!isSelected}
                                  onChange={(e) =>
                                    handleToggleManager(employee.id, "m1", e.target.checked)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0.5 }}
                                />
                                <Typography variant="body2">{employee.m1_manager_name}</Typography>
                              </Box>
                            ) : "—"}
                          </TableCell>
                          <TableCell>
                            {employee.m2_manager_name ? (
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  size="small"
                                  checked={sel.m2}
                                  disabled={!isSelected}
                                  onChange={(e) =>
                                    handleToggleManager(employee.id, "m2", e.target.checked)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0.5 }}
                                />
                                <Typography variant="body2">{employee.m2_manager_name}</Typography>
                              </Box>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/pms/designations")}
          size="large"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleAssign}
          size="large"
          disabled={submitting || ((assignmentType === "specific" || assignmentType === "other") && selectedEmployees.size === 0)}
        >
          {submitting ? "Assigning..." : "Assign to Employees"}
        </Button>
      </Box>
    </Container>
  );
};

export default AssignKRAForm;
